import { RETENTION_DAYS_PER_YEAR, type RETENTION_CLASS_VALUES, type RETENTION_PRESET_VALUES } from "@sagot/constants";

export type RetentionClass = (typeof RETENTION_CLASS_VALUES)[number];
export type RetentionPreset = (typeof RETENTION_PRESET_VALUES)[number];

/** One class as `GET /v1/retention-presets` sends it. */
export interface ClassState {
  class: RetentionClass;
  days: number;
  legal_hold: boolean;
  min_days: number;
  max_days: number;
}

/** One line of the diff a dry run returns. */
export interface ClassChange {
  class: RetentionClass;
  from: number;
  to: number;
  hold_from: boolean;
  hold_to: boolean;
}

/** What the card asks for: a preset (or none = keep the current days) and the holds it flips. */
export interface Selection {
  preset: RetentionPreset | null;
  holds: Partial<Record<RetentionClass, boolean>>;
}

/** "5 years" for whole years, "90 days" otherwise (the `count` goes to the i18n plural). */
export function describeDays(days: number): { key: "years" | "days"; count: number } {
  return days % RETENTION_DAYS_PER_YEAR === 0 ? { key: "years", count: days / RETENTION_DAYS_PER_YEAR } : { key: "days", count: days };
}

/** Holds that differ from what the server has now. */
export function changedHolds(classes: readonly ClassState[], holds: Selection["holds"]): { class: RetentionClass; legal_hold: boolean }[] {
  return classes.flatMap((item) => {
    const wanted = holds[item.class];
    return wanted === undefined || wanted === item.legal_hold ? [] : [{ class: item.class, legal_hold: wanted }];
  });
}

/** The `PUT` body of a review: the chosen preset (or `null` to keep days) plus the flipped holds. */
export function requestBody(classes: readonly ClassState[], selection: Selection, dryRun: boolean) {
  return { preset: selection.preset, classes: changedHolds(classes, selection.holds), dry_run: dryRun };
}

/** Whether a change keeps data for less time (the diff warns before applying). */
export function isShorter(change: ClassChange): boolean {
  return change.to < change.from;
}

/** Whether anything is selected that differs from the server's state. */
export function hasPendingChange(classes: readonly ClassState[], current: RetentionPreset | null, selection: Selection): boolean {
  return (selection.preset !== null && selection.preset !== current) || changedHolds(classes, selection.holds).length > 0;
}
